import { Grid, Stack, TextField, Typography } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { BuyerSellerFee } from "./BuyerSellerFee";
import type { Dispatch, SetStateAction, ChangeEvent, PropsWithChildren } from "react";
import type { AdditionalUser, AdditionalUserWithFee } from "../../../../../@types/listings/user";
import type { EditableListing } from "../../../../../@types/listings/new";

export type BuyerSellerSideProps = PropsWithChildren<{
  side: "buyer" | "seller";
  loading: boolean;
  personsFeeData: AdditionalUserWithFee[];
  organizationUsers: AdditionalUser[];
  editableFormColumns: EditableListing;
  handleChange: ({ target: { name, value } }: ChangeEvent<HTMLInputElement>) => void;
  onPersonsFeeDataChange: Dispatch<SetStateAction<AdditionalUserWithFee[]>>;
}>;

export function BuyerSellerSide({
  side,
  loading,
  children,
  personsFeeData,
  organizationUsers,
  editableFormColumns,
  handleChange,
  onPersonsFeeDataChange
}: BuyerSellerSideProps): JSX.Element {
  const { t: translate } = useTranslation();

  const {
    buyerAttorneyName,
    finalListingPrice,
    sellerAttorneyName,
    sellerAttorneyPhoneNumber,
    buyerAttorneyPhoneNumber
  } = editableFormColumns;

  const sellerDetails = {
    attorneyNameId: "sellerAttorneyName",
    attorneyPhoneNumberId: "sellerAttorneyPhoneNumber",
    attorneyName: sellerAttorneyName,
    attorneyPhoneNumber: sellerAttorneyPhoneNumber
  };

  const buyerDetails = {
    attorneyNameId: "buyerAttorneyName",
    attorneyPhoneNumberId: "buyerAttorneyPhoneNumber",
    attorneyName: buyerAttorneyName,
    attorneyPhoneNumber: buyerAttorneyPhoneNumber
  };

  const { attorneyName, attorneyNameId, attorneyPhoneNumber, attorneyPhoneNumberId } =
    side === "seller" ? sellerDetails : buyerDetails;

  return (
    <Stack gap={2}>
      <Typography variant="h5">{translate(`listingsPage.${side}Side`)}</Typography>
      <Grid container spacing={2}>
        {children && (
          <Grid xs={12} item>
            {children}
          </Grid>
        )}
        <Grid xs={12} item>
          <Stack gap={2}>
            {personsFeeData.map((personFeeData, index) => (
              <BuyerSellerFee
                side={side}
                index={index}
                loading={loading}
                personFeeData={personFeeData}
                personsFeeData={personsFeeData}
                finalListingPrice={finalListingPrice}
                organizationUsers={organizationUsers}
                onPersonsFeeDataChange={onPersonsFeeDataChange}
                key={index}
              />
            ))}
          </Stack>
        </Grid>
        <Grid xs={12} sm={6} item>
          <TextField
            fullWidth
            name={attorneyNameId}
            value={attorneyName}
            disabled={loading}
            label={translate(`listingsPage.tableColumns.${attorneyNameId}`)}
            onChange={handleChange}
          />
        </Grid>
        <Grid xs={12} sm={6} item>
          <TextField
            fullWidth
            name={attorneyPhoneNumberId}
            value={attorneyPhoneNumber}
            disabled={loading}
            label={translate(`listingsPage.tableColumns.${attorneyPhoneNumberId}`)}
            onChange={handleChange}
          />
        </Grid>
      </Grid>
    </Stack>
  );
}
